import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { ModuleService } from "../../../services/user/modules.services";
import { Editor } from "@tinymce/tinymce-react";
import striptags from "striptags";

const AddModulesComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const moduleId = location.state?.moduleId || 0;
    const isEditMode = !!moduleId;
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            moduleId: 0,
            moduleName: "",
            prefixNo: "",
            financialYear: "24-25",
            moduleNumber: "",
            termsAndConditions: "",
            storeId: selectedStore,
        },
        validationSchema: Yup.object({
            moduleName: Yup.string().required("Please enter a valid name"),
            prefixNo: Yup.string().required("Please enter a valid prefix number"),
            moduleNumber: Yup.string().required("Please enter a valid module number").matches(/^\d+$/, "Module number must be numeric"),
        }),

        onSubmit: async (values) => {
            let clearDescription = striptags(values.termsAndConditions);
            try {
                if (isEditMode) {
                    await ModuleService.updateModuleById(moduleId, {
                        ...values,
                        moduleNumber: values.moduleNumber.toString(),
                        termsAndConditions: clearDescription,
                    }).then(response => {
                        if (response) {
                            navigate("/modules");
                        }
                    })
                } else {
                    await ModuleService.addNewModule(values)
                        .then(response => {
                            console.log('Add response ', response)
                            if (response) {
                                navigate('/modules')
                            }
                        })
                }
            }
            catch (error) {
                handleNotify('Error while add Category', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT)
            }
        },
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchStatusDetail = async () => {
                try {
                    const data = await ModuleService.getModuleById(moduleId)
                    formik.setValues({
                        moduleName: data?.moduleName,
                        prefixNo: data?.prefixNo,
                        financialYear: data?.financialYear,
                        termsAndConditions: data?.termsAndConditions,
                        moduleNumber: data?.moduleNumber,
                    })
                }
                catch (error) {
                    console.log('Error fetching country details', error)
                }
            }
            fetchStatusDetail()
        }
    }, [])


    const handleBackClick = () => {
        navigate("/modules");
    };

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div
                        className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                        onClick={handleBackClick}
                    >
                        Modules</div>
                    <div className="ps-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item active" aria-current="page">
                                    {isEditMode ? "Edit Module" : "New Module"}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="ms-auto">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                            onClick={handleBackClick}
                        >
                            Back
                        </button>
                    </div>
                </div>

                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body form-container">
                        <form onSubmit={formik.handleSubmit} className="mt-4">
                            {/* Module Name Field */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="moduleName" className="form-label d-flex">
                                        Module Name <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="moduleName"
                                        name="moduleName"
                                        className={`form-control ${formik.touched.moduleName && formik.errors.moduleName ? "is-invalid" : ""}`}
                                        placeholder="Enter Module Name"
                                        value={formik.values.moduleName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.moduleName && formik.errors.moduleName && (
                                        <div className="invalid-feedback d-flex">{formik.errors.moduleName}</div>
                                    )}
                                </div>
                            </div>

                            {/* Prefix No Field */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="prefixNo" className="form-label d-flex">
                                        Prefix No <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="prefixNo"
                                        name="prefixNo"
                                        className={`form-control ${formik.touched.prefixNo && formik.errors.prefixNo ? "is-invalid" : ""}`}
                                        placeholder="Enter Prefix Number"
                                        value={formik.values.prefixNo}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.prefixNo && formik.errors.prefixNo && (
                                        <div className="invalid-feedback d-flex">{formik.errors.prefixNo}</div>
                                    )}
                                </div>
                            </div>

                            {/* Financial Year Field */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="financialYear" className="form-label d-flex">
                                        Financial Year:
                                    </label>
                                    <input
                                        type="text"
                                        id="financialYear"
                                        name="financialYear"
                                        className="form-control"
                                        placeholder="Enter Financial Year"
                                        value={formik.values.financialYear}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                            </div>

                            {/* Module Number Field */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="moduleNumber" className="form-label d-flex">
                                        Module Number <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="moduleNumber"
                                        name="moduleNumber"
                                        className={`form-control ${formik.touched.moduleNumber && formik.errors.moduleNumber ? "is-invalid" : ""}`}
                                        placeholder="Enter Module Number"
                                        value={formik.values.moduleNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.moduleNumber && formik.errors.moduleNumber && (
                                        <div className="invalid-feedback d-flex">{formik.errors.moduleNumber}</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12 mb-5">
                                <label htmlFor="termsAndConditions" className="form-label d-flex">
                                    Default Terms & Conditions :
                                </label>
                                <Editor
                                    value={formik.values.termsAndConditions}
                                    tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                                    init={{
                                        height: 300,
                                        menubar: true,
                                        plugins: [
                                            "advlist autolink lists link image charmap print preview anchor",
                                            "searchreplace visualblocks code fullscreen",
                                            "insertdatetime media table paste code help wordcount",
                                        ],
                                        toolbar:
                                            "undo redo | formatselect | bold italic backcolor | \
                                                                                  alignleft aligncenter alignright | bullist numlist outdent indent | \
                                                                                  link image",
                                    }}
                                    onEditorChange={(content) =>
                                        formik.setFieldValue("termsAndConditions", content)
                                    }
                                />
                            </div>

                            {/* Submit */}
                            <hr />
                            <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    onClick={handleBackClick}
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddModulesComponent;
